import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Container, Stack, Typography } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import appStoreIcon from '../assets/app_store_icon.png';
import appIcon from '../assets/ic_launcher_foreground_hdpi.png';
import playStoreIcon from '../assets/play_store_icon.png';

const STORE_LINKS = {
    android: 'https://play.google.com/store/apps/details?id=com.garammind.mallangpocket',
    ios: 'https://apps.apple.com/kr/app/%EB%A7%90%EB%9E%91%ED%8F%AC%EC%BC%93/id6755514501',
};

const TEXT = {
    chip: '\uB9D0\uB791\uD3EC\uCF13\uC5D0 \uC624\uC2E0 \uAC78 \uD658\uC601\uD574\uC694',
    title: '나를 위한 작은 이야기, 말랑포켓에서 시작해요',
    fallback:
        '아래에서 나에게 맞는 스토어를 선택해주세요!',
    androidButton: 'Google Play\uC5D0\uC11C \uB2E4\uC6B4\uB85C\uB4DC',
    iosButton: 'App Store\uC5D0\uC11C \uB2E4\uC6B4\uB85C\uB4DC',
    cancel: '\uC790\uB3D9 \uC774\uB3D9 \uB300\uC2E0 \uC9C1\uC811 \uC120\uD0DD\uD558\uAE30',
};

function detectPlatform() {
    if (typeof window === 'undefined') return null;

    const userAgent = window.navigator.userAgent || '';
    const platform = window.navigator.platform || '';
    const hasTouch = window.navigator.maxTouchPoints > 1;

    if (/android/i.test(userAgent)) return 'android';
    if (/iPad|iPhone|iPod/i.test(userAgent)) return 'ios';
    if (platform === 'MacIntel' && hasTouch) return 'ios';

    return null;
}

function AppDownloadPage() {
    const platform = useMemo(() => detectPlatform(), []);
    const [redirecting, setRedirecting] = useState(Boolean(platform));

    useEffect(() => {
        if (!platform || !redirecting) return undefined;

        const timerId = window.setTimeout(() => {
            window.location.href = STORE_LINKS[platform];
        }, 900);

        return () => window.clearTimeout(timerId);
    }, [platform, redirecting]);

    const platformLabel = platform === 'android' ? 'Android' : platform === 'ios' ? 'iPhone' : null;
    const description = platformLabel
        ? platformLabel +
          '\uC5D0 \uB9DE\uB294 \uC2A4\uD1A0\uC5B4\uB85C \uC7A0\uC2DC \uD6C4 \uC548\uB0B4\uD574\uB4DC\uB9B4\uAC8C\uC694.'
        : TEXT.fallback;

    return (
        <Box
            component="main"
            sx={{
                minHeight: '100vh',
                background: '#f7f8fb',
                color: '#17202a',
                display: 'flex',
                alignItems: 'center',
                py: { xs: 5, md: 8 },
            }}
        >
            <Container maxWidth="sm">
                <Stack spacing={4} alignItems="center" textAlign="center">
                    <Box
                        sx={{
                            width: 96,
                            height: 96,
                            borderRadius: 3,
                            display: 'grid',
                            placeItems: 'center',

                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            component="img"
                            src={appIcon}
                            alt=""
                            sx={{
                                width: 96,
                                height: 96,
                                objectFit: 'contain',
                                boxShadow: '0 16px 40px rgba(22, 34, 51, 0.12)',
                            }}
                        />
                    </Box>

                    <Stack spacing={1.5} alignItems="center">
                        <Chip
                            label={TEXT.chip}
                            sx={{ fontWeight: 700, backgroundColor: '#e8f1ff', color: '#175ebd' }}
                        />
                        <Typography
                            component="h1"
                            sx={{
                                fontSize: { xs: 30, sm: 38 },
                                fontWeight: 800,
                                lineHeight: 1.18,
                                letterSpacing: 0,
                                whiteSpace: 'pre-line',
                            }}
                        >
                            {'나를 위한 작은 이야기, \n말랑포켓에서 시작해요'}
                        </Typography>
                        <Typography
                            sx={{ color: '#5f6b7a', fontSize: { xs: 16, sm: 18 }, lineHeight: 1.7 }}
                        >
                            {description}
                        </Typography>
                    </Stack>

                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                        <Button
                            href={STORE_LINKS.android}
                            variant="contained"
                            size="large"
                            startIcon={<AndroidIcon />}
                            endIcon={<OpenInNewIcon />}
                            sx={{
                                minHeight: 58,
                                borderRadius: 2,
                                backgroundColor: '#1f2937',
                                fontSize: 16,
                                fontWeight: 800,
                                '&:hover': { backgroundColor: '#111827' },
                            }}
                        >
                            {TEXT.androidButton}
                        </Button>
                        <Button
                            href={STORE_LINKS.ios}
                            variant="outlined"
                            size="large"
                            startIcon={<AppleIcon />}
                            endIcon={<OpenInNewIcon />}
                            sx={{
                                minHeight: 58,
                                borderRadius: 2,
                                borderColor: '#c7d0dc',
                                color: '#17202a',
                                backgroundColor: '#ffffff',
                                fontSize: 16,
                                fontWeight: 800,
                                '&:hover': { borderColor: '#8fa1b7', backgroundColor: '#f9fbff' },
                            }}
                        >
                            {TEXT.iosButton}
                        </Button>
                    </Stack>

                    {redirecting && (
                        <Button
                            type="button"
                            onClick={() => setRedirecting(false)}
                            sx={{ color: '#5f6b7a', fontWeight: 700 }}
                        >
                            {TEXT.cancel}
                        </Button>
                    )}

                    <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Box
                            component="img"
                            src={playStoreIcon}
                            alt="Google Play"
                            sx={{ height: 24, width: 'auto' }}
                        />
                        <Box
                            component="img"
                            src={appStoreIcon}
                            alt="App Store"
                            sx={{ height: 24, width: 'auto' }}
                        />
                    </Stack>
                </Stack>
            </Container>
        </Box>
    );
}

export default AppDownloadPage;
